import { DS_COLOR } from '@calm/evexia-design-tokens';
import { MediaProgramGuide } from '@calm/evexia-web-sdk';
import {
    RegistrationFlow,
    RegistrationFlowStep,
} from '@rhg/hooks/use-registration-context/types';
import { useRegistrationDialog } from '@rhg/hooks/use-registration-dialog';
import { useUserBlocker } from '@rhg/hooks/use-user-blocker';
import { UserBlocker } from '@rhg/hooks/use-user-blocker/types';
import commonMessages from '@rhg/lang/messages/common';
import programMessages from '@rhg/lang/messages/program';
import { PaletteMode, Theme, useTheme } from '@rhg/libs/material';
import {
    HeartFilled,
    HeartOutline,
    LockThinOutline,
} from '@rhg/libs/material/icons';
import { MediaType, getMediaType } from '@rhg/utils/media';
import { ProgramState } from '@rhg/utils/programs';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useActiveCircle } from '../use-active-circle';
import useConfirmation from '../use-confirmation';
import { ProgramContextState } from '../use-program-context/types';
import { useRecentAndFavoriteGuides } from '../use-recent-and-favorite-guides';
import useToastDialog from '../use-toast-dialog';
import { useUnsponsoredDialog } from '../use-unsponsored-dialog';
import { useUpdateGuide } from '../use-update-guide';

const styleToastDialogIconContainer = (prefersDarkMode: boolean) => ({
    width: 64,
    height: 64,
    p: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: (theme: Theme) =>
        prefersDarkMode || theme.palette.mode === 'dark'
            ? DS_COLOR.LIGHT_SECONDARY_MAIN
            : DS_COLOR.LIGHT_SECONDARY_LIGHT,
});
const styleToastDialogContentContainer = (prefersDarkMode: boolean) => ({
    px: 6,
    py: 4,
    backgroundColor: (theme: Theme) =>
        prefersDarkMode || theme.palette.mode === 'dark'
            ? DS_COLOR.DARK_BACKGROUND_PAPER
            : 'background.paper',
});
const styleToastDialogTitleWrapper = { mb: 0 };
const styleToastDialogTitle = { mb: 0 };
const styleToastDialogIcon = (mode: PaletteMode) => ({
    height: 40,
    width: 40,
    color:
        mode === 'light'
            ? DS_COLOR.LIGHT_SECONDARY_MAIN
            : DS_COLOR.DARK_SECONDARY_LIGHT,
});

interface Props {
    programId: string;
    programType: ProgramContextState['programType'];
    packClass: ProgramContextState['packClass'];
    programState: ProgramContextState['programState'];
    onJoin?: () => void;
    onSuccess?: (isFavorite: boolean) => void;
}

export type FavoriteGuideFn = (
    guide: MediaProgramGuide,
    isFavorite: boolean,
    prefersDarkToast?: boolean
) => void;

export function useFavoriteGuide({
    programId,
    programType,
    packClass,
    programState,
    ...props
}: Props) {
    const { formatMessage } = useIntl();
    const theme = useTheme();
    const { userBlocker } = useUserBlocker();
    const { showUnsponsoredDialog } = useUnsponsoredDialog();
    const { updateGuideAsync } = useUpdateGuide();
    const { activeCircleId } = useActiveCircle();
    const { refresh: refreshRecentsAndFavorites } =
        useRecentAndFavoriteGuides(activeCircleId);
    const { showRegistrationDialog } = useRegistrationDialog();
    const { confirm } = useConfirmation();
    const { toast } = useToastDialog();

    const isUnsponsored = useMemo(
        () => userBlocker === UserBlocker.Unsponsored,
        [userBlocker]
    );

    const canInteract = useMemo(
        () =>
            programId &&
            (programType !== 'clinical_program' ||
                programState === ProgramState.InProgress ||
                programState === ProgramState.Completed),
        [programId, programState, programType]
    );

    const canFavorite = useMemo(
        () => !isUnsponsored && !!canInteract,
        [canInteract, isUnsponsored]
    );

    const handleUnsponsored = useCallback(async () => {
        if (await showUnsponsoredDialog()) {
            showRegistrationDialog({
                currentStep: RegistrationFlowStep.AccessCode,
                registrationFlow: RegistrationFlow.ExistingRegistration,
            });
        }
    }, [showUnsponsoredDialog, showRegistrationDialog]);

    const toastDialogIconStyle = useCallback(
        (isVideo: boolean) =>
            styleToastDialogIcon(isVideo ? 'dark' : theme.palette.mode),
        [theme.palette.mode]
    );

    const addedText = formatMessage(
        commonMessages['common.added_to_favorites']
    );
    const removedText = formatMessage(
        commonMessages['common.removed_from_favorites']
    );

    const onFavoriteSuccess = useCallback(
        (isFavorite: boolean, isVideo: boolean) => {
            toast({
                title: isFavorite ? addedText : removedText,
                icon: isFavorite ? (
                    <HeartFilled style={toastDialogIconStyle(isVideo)} />
                ) : (
                    <HeartOutline style={toastDialogIconStyle(isVideo)} />
                ),
                SimpleDialogProps: {
                    titleVariant: 'h6',
                    titleColor:
                        isVideo || theme.palette.mode === 'dark'
                            ? DS_COLOR.DARK_TEXT_BODY
                            : 'text.body',
                    dialogMaxWidth: 343,
                    iconContainerStyle: styleToastDialogIconContainer(isVideo),
                    contentContainerStyle:
                        styleToastDialogContentContainer(isVideo),
                    titleWrapperStyle: styleToastDialogTitleWrapper,
                    titleStyle: styleToastDialogTitle,
                },
            });
            props?.onSuccess?.(isFavorite);
        },
        // TODO: fix this eslint-disable
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props, formatMessage, theme.palette.mode, toast, toastDialogIconStyle]
    );

    const favoriteGuide = useCallback<FavoriteGuideFn>(
        async (guide, isFavorite, prefersDarkToast) => {
            const isVideo = getMediaType(guide) === MediaType.Video;
            const useDarkToast =
                typeof prefersDarkToast === 'boolean'
                    ? prefersDarkToast
                    : isVideo;

            if (isUnsponsored) {
                handleUnsponsored();
            } else if (canInteract) {
                await updateGuideAsync({
                    guideId: guide.id,
                    packClass,
                    programId: programId,
                    request: {
                        isFavorite,
                    },
                });
                refreshRecentsAndFavorites();
                onFavoriteSuccess(isFavorite, useDarkToast);
            } else if (
                programType === 'clinical_program' &&
                (await confirm({
                    noCancelButton: true,
                    icon: <LockThinOutline fontSize="large" color="inherit" />,
                    iconColor: 'info',
                    title: formatMessage(
                        programMessages['program.join_program']
                    ),
                    body: formatMessage(
                        programMessages['program.before_favoriting_join']
                    ),
                    confirmLabel: formatMessage(
                        programMessages['program.join_program']
                    ),
                    trackDialog: 'JoinBeforeFavoritingProgram',
                    confirmTrackButton: 'DialogJoinProgram',
                }))
            ) {
                props?.onJoin?.();
            }
        },
        [
            refreshRecentsAndFavorites,
            canInteract,
            confirm,
            handleUnsponsored,
            isUnsponsored,
            onFavoriteSuccess,
            packClass,
            programId,
            programType,
            props,
            formatMessage,
            updateGuideAsync,
        ]
    );

    return { favoriteGuide, canFavorite };
}
